<template>
  <div v-if="task != null">
    <h6 class="mb-2">Movimiento planificado</h6>

    <v-chip size="small" class="mb-3">
      {{ task.movement_type === taskService.MOVEMENT_TYPE_PRODUCT ? "Productos seleccionados" : "LPN completo" }}
    </v-chip>

    <div class="d-flex align-center gap-3 mt-1 mb-4">
      <div v-if="task.origin_lpn || task.origin_ubicacion" class="d-flex flex-column">
        <span class="font-weight-bold text-caption">Origen</span>
        <span v-if="task.origin_lpn">{{ task.origin_lpn.code }}</span>
        <span v-if="task.origin_ubicacion" class="text-caption text-grey-600 nowrap">
          {{ task.origin_ubicacion.code }}
          <template v-if="task.origin_ubicacion.zona">- {{ task.origin_ubicacion.zona.label }}</template>
        </span>
      </div>

      <v-divider v-if="(task.origin_lpn || task.origin_ubicacion) && (task.destination_lpn || task.destination_ubicacion)" vertical class="mx-2"></v-divider>

      <div v-if="task.destination_lpn || task.destination_ubicacion" class="d-flex flex-column">
        <span class="font-weight-bold text-caption">Destino</span>
        <span v-if="task.destination_lpn">{{ task.destination_lpn.code }}</span>
        <span v-if="task.destination_ubicacion" class="text-caption text-grey-600 nowrap">
          {{ task.destination_ubicacion.code }}
          <template v-if="task.destination_ubicacion.zona">- {{ task.destination_ubicacion.zona.label }}</template>
        </span>
      </div>
    </div>

    <!-- Datos de reposicion -->
    <div v-if="task.product_reposicion_location">
      <h6 class="mb-2">Datos de reposición</h6>
      <v-row class="mb-2">
        <v-col cols="12" md="3">
          <div class="text-caption text-medium-emphasis">Cantidad sugerida</div>
          <div>{{ task.suggested_amount != null ? task.suggested_amount : "-" }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="task.product_reposicion_location.product">
          <div class="text-caption text-medium-emphasis">Producto</div>
          <div>{{ task.product_reposicion_location.product.name }}</div>
          <span class="text-caption text-grey-600">SKU: {{ task.product_reposicion_location.product.sku }}</span>
        </v-col>
        <v-col cols="12" md="3" v-if="task.product_reposicion_location.ubicacion">
          <div class="text-caption text-medium-emphasis">Ubicación de picking</div>
          <div>{{ task.product_reposicion_location.ubicacion.code }}</div>
        </v-col>
        <v-col cols="12" md="3">
          <div class="text-caption text-medium-emphasis">Stock mín. / máx.</div>
          <div>{{ task.product_reposicion_location.stock_min != null ? task.product_reposicion_location.stock_min : "-" }} / {{ task.product_reposicion_location.stock_max != null ? task.product_reposicion_location.stock_max : "-" }}</div>
        </v-col>
        <v-col cols="12" md="3" v-if="task.product_reposicion_location.rule">
          <div class="text-caption text-medium-emphasis">Regla</div>
          <div>#{{ task.product_reposicion_location.rule.id }}</div>
        </v-col>
        <v-col cols="12" md="3">
          <div class="text-caption text-medium-emphasis">Activa</div>
          <v-chip size="small" :color="task.product_reposicion_location.activo ? 'success' : 'grey'">
            {{ task.product_reposicion_location.activo ? "Sí" : "No" }}
          </v-chip>
        </v-col>
      </v-row>
    </div>

    <!-- Items a mover -->
    <h6 class="mb-2">Ítems a mover</h6>
    <v-alert v-if="task.movement_type !== taskService.MOVEMENT_TYPE_PRODUCT" type="info" variant="tonal" density="compact">
      Se mueve el LPN completo con todo su contenido.
    </v-alert>
    <v-data-table
      v-else-if="task.items && task.items.length > 0"
      :headers="headers"
      :items="task.items"
      hide-default-footer
      :items-per-page="-1"
      density="comfortable"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>
            <span v-if="item.product">{{ item.product.name }}</span>
            <br />
            <span v-if="item.product" class="text-caption text-grey-600">SKU: {{ item.product.sku }}</span>
          </td>
          <td>
            {{ item.amount }}
            <span v-if="item.product_presentation && item.product_presentation.presentation_type" class="text-caption text-grey-600">
              {{ getPresentationTypeName(item.amount, item.product_presentation.presentation_type.id, item.product_presentation.presentation_type.name) }}
            </span>
          </td>
          <td>{{ item.storage_units_amount != null ? item.storage_units_amount : "-" }}</td>
          <td>{{ item.lote || "-" }}</td>
          <td>{{ item.serie || "-" }}</td>
          <td>{{ item.expiration_date || "-" }}</td>
          <td>{{ item.manufacture_date || "-" }}</td>
          <td>
            <v-chip v-if="item.quality" size="small" color="primary">{{ item.quality.name }}</v-chip>
            <span v-else>-</span>
          </td>
          <td v-for="d in descriptivos" :key="d.key">{{ item[d.key] || "-" }}</td>
        </tr>
      </template>
    </v-data-table>
    <v-alert v-else type="info" variant="tonal" density="compact">Sin ítems.</v-alert>
  </div>
</template>

<script>
import { taskService } from "@/libs/ws/taskService";
import GenericMixin from "@/mixins/GenericMixin.vue";

export default {
  name: "TaskViewDetalleTransferencia",
  mixins: [GenericMixin],
  props: {
    task: Object,
    descriptivos: { type: Array, default: () => [] },
  },
  data() {
    return {
      taskService: taskService,
    };
  },
  computed: {
    headers() {
      const fixed = [
        { title: "Producto", key: "product", sortable: false },
        { title: "Cantidad", key: "amount", sortable: false },
        { title: "Almacenamiento", key: "storage_units_amount", sortable: false },
        { title: "Lote", key: "lote", sortable: false },
        { title: "Serie", key: "serie", sortable: false },
        { title: "Vencimiento", key: "expiration_date", sortable: false },
        { title: "Fabricación", key: "manufacture_date", sortable: false },
        { title: "Calidad", key: "quality", sortable: false },
      ];
      return fixed.concat(this.descriptivos.map((d) => ({ title: d.label, key: d.key, sortable: false })));
    },
  },
};
</script>

<style scoped>
.gap-3 > * + * {
  margin-left: 12px;
}
.nowrap {
  white-space: nowrap;
}
</style>
